import { useState, useRef } from 'react';
import { fetchInstallmentGroups, fetchBinDetail } from "../../../../../api/requests/payment-requests";
import { resolveBankLogo, resolveCardSchemaLogo } from '../../../../../utils/bankLogo';

// Bu yardımcı fonksiyon PaymentForm'dan alınmıştır
const toPct = (v) => {
    if (v == null) return 0;
    if (typeof v === 'number') return v;
    const cleaned = v.toString().replace(',', '.');
    const num = parseFloat(cleaned);
    return isNaN(num) ? 0 : num;
};

export const useBinInstallments = (companyId, validation, updateCardVisual, getTotal, formatTRY) => {
    const [binQueryStatus, setBinQueryStatus] = useState('');
    const [paytrError, setPaytrError] = useState('');
    const [detectedCardBrand, setDetectedCardBrand] = useState(null);
    const [installmentTable, setInstallmentTable] = useState('');
    const [cariGroups, setCariGroups] = useState([]);
    // State başlangıçta 0 kalmalı, fetch sonrası güncellenecek.
    const [selectedCariGroup, setSelectedCariGroup] = useState(0);
    const [showCariGroup, setShowCariGroup] = useState(false);
    const [lastBin, setLastBin] = useState(null);
    const binController = useRef();

    const resetCardVisual = () => {
        updateCardVisual('brand', '');
        updateCardVisual('type', '');
        updateCardVisual('bank', '');
        updateCardVisual('schema', '');
    };

    const clearBinQueryResults = () => {
        resetCardVisual();
        setInstallmentTable('');
        setShowCariGroup(false);
        setBinQueryStatus('');
        setPaytrError('');
        setDetectedCardBrand(null);
    };

    const renderInstallmentTable = (
        installmentRates,
        amountOverride,
        currentSelectedInstallment,
        cardBrandOverride // yeni parametre
    ) => {
        const amount = typeof amountOverride === 'number'
            ? amountOverride
            : validation.values.amount;

        const effectiveBrand = (cardBrandOverride || detectedCardBrand || '').toString().toUpperCase();

        if (isNaN(amount) || amount <= 0 || !effectiveBrand) {
            return { html: '', newInstallment: currentSelectedInstallment };
        }

        let cardBrandRates = (installmentRates || [])
            .filter(r => {
                if (!r || !r.enabled) return false;
                const rateBrand = (r.cardBrand || '').toString().toUpperCase();
                return rateBrand === effectiveBrand;
            })
            .sort((a, b) => a.installmentCount - b.installmentCount);

        let effectiveRates = cardBrandRates;
        if (!effectiveRates.length) {
            effectiveRates = (installmentRates || [])
                .filter(r => r && r.enabled)
                .sort((a, b) => a.installmentCount - b.installmentCount);
        }

        if (!effectiveRates.length) {
            return {
                html: `<div class="alert alert-warning">Bu kart için aktif taksit seçeneği bulunamadı.</div>`,
                newInstallment: { count: 1, commission: 0 }
            };
        }

        const formatTurkish = (num) =>
            num.toLocaleString('tr-TR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }) + ' TL';

        let html = '<label class="fw-bold mb-2">TAKSİT SEÇİMİ</label>';
        html += '<table class="table table-bordered"><thead><tr><th></th><th>Aylık</th><th>Toplam</th></tr></thead><tbody>';

        effectiveRates.forEach(rate => {
            const { installmentCount: count, commissionPercent } = rate;
            const divisor = (100 - commissionPercent) / 100;
            const total = amount / divisor;
            const monthly = count > 1 ? total / count : 0;

            html += `
          <tr>
            <td>
              <input type="radio" name="installment_radio" value="${count}" 
                ${currentSelectedInstallment.count === count ? 'checked' : ''}
                data-commission="${commissionPercent.toFixed(2)}" />
              ${count === 1 ? 'Peşin' : count + ' Tek Çekim'}
            </td>
            <td>${count > 1 ? formatTurkish(monthly) : '-'}</td>
            <td>${formatTurkish(total)}</td>
          </tr>`;
        });

        html += '</tbody></table>';

        const first = effectiveRates[0];
        const newInstallment = {
            count: first.installmentCount,
            commission: first.commissionPercent
        };

        validation.setFieldValue('installment_count', first.installmentCount.toString());
        updateCardVisual('amount', formatTRY(getTotal(amount, first.commissionPercent)));

        return { html, newInstallment };
    };

    const fetchMyAccountGroups = async (cardBrand, amount) => {
        try {
            const res = await fetchInstallmentGroups();
            const result = await res.json();
            if (res.status !== 200) throw new Error(result.message || "Grup sorgusu başarısız.");

            const groups = result.data || [];
            if (groups.length === 0) {
                setInstallmentTable('<div class="alert alert-warning">Hiç cari/bayi grup bulunamadı.</div>');
                return { newInstallment: { count: 1, commission: 0 } };
            }

            // DÜZELTME BAŞLANGICI: companyId'ye ait grubu varsayılan olarak seç
            let defaultIndex = 0;
            const companySpecificIndex = groups.findIndex(g => {
                const gCompanyId = g.companyId || g.company?.id || null;
                // Hook'a gelen companyId'yi kullanarak en alakalı grubu bul
                return gCompanyId === companyId;
            });

            if (companySpecificIndex !== -1) {
                defaultIndex = companySpecificIndex;
            }
            // DÜZELTME SONU

            setCariGroups(groups);
            setSelectedCariGroup(defaultIndex);
            setShowCariGroup(true);

            const defaultGroupRates = groups[defaultIndex]?.installmentRates || [];
            if (cardBrand && defaultGroupRates.length > 0) {
                return renderInstallmentTable(
                    defaultGroupRates,
                    amount,
                    { count: 1, commission: 0 },
                    cardBrand // buraya brandUpper geliyor
                );
            }
            return { html: '', newInstallment: { count: 1, commission: 0 } };

        } catch (err) {
            setInstallmentTable('<div class="alert alert-danger">Taksit grup verisi alınamadı.</div>');
            return { newInstallment: { count: 1, commission: 0 } };
        }
    };

    const queryBIN = async (bin, amount) => {
        binController.current?.abort();
        binController.current = new AbortController();
        try {
            if (!companyId) return { newInstallment: { count: 1, commission: 0 } };

            setBinQueryStatus('loading');
            setPaytrError('');

            const res = await fetchBinDetail(bin, companyId, binController.current.signal);
            const { statusCode, data, message } = await res.json();

            if (statusCode === 404) {
                setBinQueryStatus('paytr_not_found');
                setPaytrError(message || 'PayTR ayarları bulunamadı');
                setInstallmentTable(`<div class="alert alert-warning">${message || 'PayTR ayarları bulunamadı.'}</div>`);
                resetCardVisual();
                setShowCariGroup(false);
                return { newInstallment: { count: 1, commission: 0 } };
            }

            setBinQueryStatus('success');
            if (statusCode !== 200 || !data || data.status !== 'success') {
                throw new Error(message || "BIN lookup failed");
            }

            const { bank, brand, schema, cardType } = data;
            const brandUpper = brand.toUpperCase();
            setDetectedCardBrand(brandUpper);

            updateCardVisual('bank', bank);
            updateCardVisual('brand', brandUpper);
            updateCardVisual('type', cardType.toUpperCase());
            updateCardVisual('schema', schema);
            updateCardVisual('bankLogo', resolveBankLogo(bank));
            updateCardVisual('cardLogo', resolveCardSchemaLogo(schema));

            // Taksitleri getir ve yeni seçili taksiti döndür
            return await fetchMyAccountGroups(brandUpper, amount);

        } catch (err) {
            console.error("BIN sorgu hatası:", err);
            setBinQueryStatus('error');
            resetCardVisual();
            setInstallmentTable(`<div class="alert alert-danger">Kart bilgileri alınamadı.</div>`);
            setShowCariGroup(false);
            return { newInstallment: { count: 1, commission: 0 } };
        }
    };

    const handleCariGroupChange = (e, amount) => {
        const idx = parseInt(e.target.value);
        setSelectedCariGroup(idx);
        let newInstallment = { count: 1, commission: 0 };
        if (cariGroups[idx] && detectedCardBrand) {
            const result = renderInstallmentTable(cariGroups[idx].installmentRates, amount, newInstallment);
            setInstallmentTable(result.html);
            newInstallment = result.newInstallment;
        }
        return { selectedCariGroup: idx, newInstallment };
    };

    return {
        binQueryStatus,
        paytrError,
        detectedCardBrand,
        installmentTable,
        setInstallmentTable, // Dışarıdan güncelleme için
        cariGroups,
        selectedCariGroup,
        showCariGroup,
        lastBin,
        setLastBin,
        clearBinQueryResults,
        queryBIN,
        renderInstallmentTable, // Tutar değiştiğinde tetiklemek için
        handleCariGroupChange,
    };
};